import { getLookupMap } from "@citrine/utils/helpers";

import type { GEMDDataSourceID, TableData } from "../data-source";
import { getDataIdentifier } from "../data-source";
import type { Column, GEMTableConfig } from "../gem-table-config";
import {
  isAttributeTemplateVariable,
  isIdentityColumn,
  isIngredientQuantityVariable,
  isMeanColumn,
  isTerminalMaterialIdentifierVariable,
  isTerminalMaterialInfoVariable,
} from "../gem-table-config";
import type {
  ListMaterialMetadataReply,
  MaterialClassification,
  MaterialClassificationCounts,
} from "../vquery";

import type { IGEMTable, IGEMTableContentSummary } from "./gem-table";

/**
 * Converts a GEM Table object into a Table Data object
 */
export function gemTableToDataSource(gemTable: IGEMTable): TableData {
  return {
    type: "hosted_table_data_source",
    table_id: gemTable.table.id,
    table_version: gemTable.table.version,
  };
}

/**
 * Converts a GEM Table object into a GEMDDataSourceID
 */
export function gemTableToDataSourceId(gemTable: IGEMTable): GEMDDataSourceID {
  return getDataIdentifier(gemTableToDataSource(gemTable));
}

/**
 * the citrine id is the last value of the array
 */
export function getCitrineIdFromTableRow(row: string[]) {
  return row[row.length - 1];
}

export const getMaterialColumnName = (index: number) => `Material ${index + 1}`;

export function getMaterialNameLookupMap(
  table: IGEMTableContentSummary,
  config: GEMTableConfig,
) {
  const materialNameColumnIndex = getMaterialNameColumnIndex(config);

  return Object.fromEntries(
    table.table_content.rows.map((row, index): [string, string] => [
      getCitrineIdFromTableRow(row),
      row[materialNameColumnIndex] ?? getMaterialColumnName(index),
    ]),
  );
}

/**
 * Returns a column index that can be used to extract a "material name" from the
 * rows of the CSV content summary. Tries first to find a `root_info` Variable,
 * and falls back to finding a `root_id` Variable. If neither of those variables
 * exist, returns -1.
 */
export function getMaterialNameColumnIndex(config: GEMTableConfig): number {
  const { columns, variables } = config.version.ara_definition;

  const materialNameVariable = variables.find(
    (variable) =>
      isTerminalMaterialInfoVariable(variable) && variable.field === "NAME",
  );
  const materialNameColumnIndex = materialNameVariable
    ? columns.findIndex(
        (column) =>
          isIdentityColumn(column) &&
          column.data_source === materialNameVariable.name,
      )
    : -1;

  if (materialNameColumnIndex > -1) {
    return materialNameColumnIndex;
  }

  const materialIdVariable = variables.find(
    isTerminalMaterialIdentifierVariable,
  );

  if (materialIdVariable) {
    return columns.findIndex(
      (column) =>
        isIdentityColumn(column) &&
        column.data_source === materialIdVariable.name,
    );
  }

  return -1;
}

export type RowMaterialTypeMap = Partial<
  Record<string, MaterialClassification>
>;

export interface CategorizedRowIds {
  counts: MaterialClassificationCounts;
  rowMap: RowMaterialTypeMap;
}

/**
 * Categorizes rows in the table as ingredient, intermediate, or terminal. Also
 * returns a count of how many of each material type are in the table.
 * */
export function categorizeRows(
  table: IGEMTableContentSummary,
  materialMetadata: ListMaterialMetadataReply,
): CategorizedRowIds {
  /** Metadata lookup for the entire untruncated table */
  const metadataLookup = getLookupMap(
    materialMetadata.material_metadata,
    (metadata) => metadata.identifiers.id,
  );

  return table.table_content.rows.reduce<CategorizedRowIds>(
    (acc, row) => {
      const materialId = getCitrineIdFromTableRow(row);
      const type = metadataLookup[materialId]?.classification ?? "terminal";

      acc.rowMap[getCitrineIdFromTableRow(row)] = type;
      acc.counts[type]++;
      return acc;
    },
    {
      counts: {
        raw: 0,
        intermediate: 0,
        terminal: 0,
      },
      rowMap: {},
    },
  );
}

/**
 * Returns the table headers that represent ingredient quantities
 *
 * @param table - used for accessing the content_summary headers
 * @param config - used for accessing the columns and variables
 * @param terminalMaterialRows - used for determining whether ingredient
 *   quantities are associated with terminal or non-terminal materials
 */
export function getIngredientQuantityHeaders(
  table: IGEMTableContentSummary,
  config: GEMTableConfig,
  terminalMaterialRows: string[][],
) {
  const { columns, variables } = config.version.ara_definition;

  // The `data_source` attribute of a Column is not necessarily unique, therefore
  // we need to filter out non-mean columns before doing variable name matching.
  const meanColumnsWithIndexes = columns
    .map((column, index): [Column, number] => [column, index])
    .filter(([column]) => isMeanColumn(column));

  const ingredientQuantityVariableNames = new Set(
    variables.filter(isIngredientQuantityVariable).map(({ name }) => name),
  );

  const ingredientQuantityColumnIndexes = meanColumnsWithIndexes
    .filter(([{ data_source }]) =>
      ingredientQuantityVariableNames.has(data_source),
    )
    .map(([, index]) => index);

  // Return the headers that have values populated for the ingredient quantity
  // indexes in the terminal material rows.
  return ingredientQuantityColumnIndexes
    .filter((colIdx) => terminalMaterialRows.some((row) => row[colIdx]))
    .map((colIdx) => table.table_content.headers[colIdx]);
}

/**
 * Returns the table headers that represent real material attributes
 *
 * @param table - used for accessing the content_summary headers
 * @param config - used for accessing the columns and variables
 * @param rows - can be used to downscope the returned real attributes to a
 *   subset of rows - i.e. only terminal or only ingredient row attributes
 */
export function getRealMaterialAttributeHeaders(
  table: IGEMTableContentSummary,
  config: GEMTableConfig,
  rowSubset: string[][] = table.table_content.rows,
) {
  const { columns, variables } = config.version.ara_definition;

  // The `data_source` attribute of a Column is not necessarily unique, therefore
  // we need to filter out non-mean columns before doing variable name matching.
  const meanColumnsWithIndexes = columns
    .map((column, index): [Column, number] => [column, index])
    .filter(([column]) => isMeanColumn(column));

  const attributeVariableNames = new Set(
    variables.filter(isAttributeTemplateVariable).map(({ name }) => name),
  );

  const realAttributeTemplateColumnIndexes = meanColumnsWithIndexes
    .filter(([{ data_source }]) => attributeVariableNames.has(data_source))
    .map(([, index]) => index);

  // Return the headers that have values populated for the real attribute indexes
  // in the specified rowSubset.
  return realAttributeTemplateColumnIndexes
    .filter((colIdx) => rowSubset.some((row) => row[colIdx]))
    .map((colIdx) => table.table_content.headers[colIdx]);
}
